import React, { useRef, useEffect, useState } from "react";
import { STATUS_CONFIG } from "./status.config";

export type StatusModule =
  | "text"
  | "payments"
  | "rtp"
  | "beneficiary"
  | "bulk"
  | "directDebit";

interface StatusBadgeProps {
  module: StatusModule; // . required
  statusCode?: number | string;
  className?: string;
  textClassName?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  module,
  statusCode,
  className = "",
  textClassName = "gx-p10-sb",
}) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);

  const { textStatuses, textStatusAliases } = STATUS_CONFIG;

  let label = "Unknown";
  let style = "bg-gray-100 text-gray-600";

  // =========================
  // 1️⃣ TEXT MODULE (EXPLICIT)
  // =========================
  if (module === "text" && typeof statusCode === "string") {
    const raw = statusCode.trim();

    const resolved = textStatusAliases[raw] ?? raw;

    if (textStatuses[raw]) {
      label = resolved;
      style = textStatuses[raw];
    }
  }

  // =========================
  // 2️⃣ NUMERIC MODULE STATUS
  // =========================
  else if (module !== "text" && !Number.isNaN(Number(statusCode))) {
    const moduleData = STATUS_CONFIG[module] as
      | {
          labels?: Record<number, string>;
          styles?: Record<number, string>;
        }
      | undefined;

    if (moduleData?.labels && moduleData?.styles) {
      const code = Number(statusCode);
      label = moduleData.labels[code] ?? label;
      style = moduleData.styles[code] ?? style;
    }
  }

  // =========================
  // 3️⃣ TEXT FALLBACK
  // =========================
  else if (typeof statusCode === "string") {
    const raw = statusCode.trim();

    const resolved =
      textStatusAliases[raw] ??
      raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();

    if (textStatuses[resolved]) {
      label = resolved;
      style = textStatuses[resolved];
    }
  }
  
  // =========================
  // 4️⃣ OVERFLOW DETECTION
  // =========================
  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const textWidth = textRef.current.scrollWidth;
        const overflow = textWidth > containerWidth;

        setIsOverflowing(overflow);

        if (overflow) {
          setScrollDistance(textWidth - containerWidth + 20);
        }
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [label]);

  // =========================
  // RENDER
  // =========================
  return (
    <span
      ref={containerRef}
      className={`gx-status-badge ${textClassName} ${style} ${className}`}
      data-overflowing={isOverflowing}
    >
      <span
        ref={textRef}
        className="status-text"
        style={
          {
            "--scroll-distance": `-${scrollDistance}px`,
          } as React.CSSProperties
        }
      >
        {label}
      </span>
    </span>
  );
};

export default StatusBadge;
